// ข้อมูลที่อยู่ไทย (จังหวัด → เขต/อำเภอ → แขวง/ตำบล + รหัสไปรษณีย์) จาก jos-cowork-api — endpoint public ไม่ต้อง login
// จำผลไว้ในหน่วยความจำ (คำขอซ้ำไม่ยิง API ซ้ำ) — ที่มาข้อมูลอยู่ที่ backend ที่เดียว (app/data/thai_address.json)
import { useEffect, useState } from 'react'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5050/api'

export interface ThaiSubdistrict {
  name: string
  zip: number
}

// ชื่อย่อที่อาจค้างอยู่ในข้อมูลเดิมของผู้ใช้ → ชื่อทางการ (ตรงกับ backend address_service.PROVINCE_ALIAS)
const PROVINCE_ALIAS: Record<string, string> = { 'อยุธยา': 'พระนครศรีอยุธยา' }
export const normalizeProvince = (name: string | null | undefined): string => PROVINCE_ALIAS[name ?? ''] ?? name ?? ''

const cache = new Map<string, Promise<unknown>>()

function fetchCached<T>(path: string): Promise<T> {
  let p = cache.get(path) as Promise<T> | undefined
  if (!p) {
    p = fetch(`${API_URL}${path}`)
      .then((res) => res.json())
      .then((json: { success: boolean; data: T; error?: string }) => {
        if (!json.success) throw new Error(json.error || 'โหลดข้อมูลที่อยู่ไม่สำเร็จ')
        return json.data
      })
      .catch((err) => {
        cache.delete(path) // ล้มเหลวแล้วอย่าจำ — ให้ลองใหม่ได้
        throw err
      })
    cache.set(path, p)
  }
  return p
}

export const getProvinces = () => fetchCached<string[]>('/address/provinces')
export const getDistricts = (province: string) =>
  fetchCached<string[]>(`/address/districts?province=${encodeURIComponent(province)}`)
export const getSubdistricts = (province: string, district: string) =>
  fetchCached<ThaiSubdistrict[]>(
    `/address/subdistricts?province=${encodeURIComponent(province)}&district=${encodeURIComponent(district)}`,
  )

interface AddressList<T> {
  data: T[]
  loading: boolean
  error: boolean
}

// โหลดรายการตามค่าที่เลือกไว้ — ไม่มี key (ยังไม่ได้เลือกระดับก่อนหน้า) = รายการว่าง ไม่ยิง API
function useAddressList<T>(key: string | null, load: () => Promise<T[]>): AddressList<T> {
  const [state, setState] = useState<{ key: string | null; data: T[]; error: boolean }>({ key: null, data: [], error: false })

  useEffect(() => {
    if (!key) return
    let cancelled = false
    load()
      .then((data) => { if (!cancelled) setState({ key, data, error: false }) })
      .catch(() => { if (!cancelled) setState({ key, data: [], error: true }) })
    return () => { cancelled = true }
    // load ผูกกับ key อยู่แล้ว (สร้างจากค่าเดียวกัน) จึงไม่ใส่เป็น dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (!key) return { data: [], loading: false, error: false }
  if (state.key !== key) return { data: [], loading: true, error: false }
  return { data: state.data, loading: false, error: state.error }
}

export const useProvinces = () => useAddressList<string>('provinces', getProvinces)

export const useDistricts = (province: string) =>
  useAddressList<string>(province ? `d:${province}` : null, () => getDistricts(province))

export const useSubdistricts = (province: string, district: string) =>
  useAddressList<ThaiSubdistrict>(
    province && district ? `s:${province}|${district}` : null,
    () => getSubdistricts(province, district),
  )
